import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BlobAccent,
  BlobSecondary,
  MorphingBackground,
} from './vio-card.styles';

export type VioCardProps = {
  title?: string;
  subtitle?: string;
  loading?: boolean;
  loadingMessage?: string;
  children?: ReactNode;
  'data-testid'?: string;
};

const VioCard = ({
  title,
  subtitle,
  loading = false,
  loadingMessage,
  children,
  'data-testid': dataTestId = 'VioCard',
}: VioCardProps) => {
  const { t } = useTranslation();

  const resolvedLoadingMessage = loadingMessage ?? t('vioCard.loading');
  const showHeader = Boolean(title) || Boolean(subtitle) || loading;
  const showMorph = loading || Boolean(children);
  const morphFrozen = showMorph && !loading;

  return (
    <Card
      data-testid={dataTestId}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...(showMorph && { minHeight: 180 }),
      }}
    >
      {showMorph && (
        <MorphingBackground
          aria-hidden
          data-testid={`${dataTestId}.MorphingBackground`}
          data-frozen={morphFrozen ? 'true' : undefined}
        >
          <BlobSecondary />
          <BlobAccent />
        </MorphingBackground>
      )}
      <CardContent sx={{ position: 'relative' }}>
        <Stack spacing={2}>
          {showHeader && (
            <Stack spacing={0.5}>
              {title && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AutoAwesomeIcon
                    fontSize="small"
                    color="primary"
                    data-testid={`${dataTestId}.AiIcon`}
                  />
                  <Typography variant="h6" component="div">
                    {title}
                  </Typography>
                </Stack>
              )}
              {loading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  data-testid={`${dataTestId}.LoadingMessage`}
                >
                  {resolvedLoadingMessage}
                </Typography>
              ) : (
                subtitle && (
                  <Typography variant="body2" color="text.secondary">
                    {subtitle}
                  </Typography>
                )
              )}
            </Stack>
          )}
          {!loading && children}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default VioCard;
